import React from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  Grid,
  Badge,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CategoryIcon from "@mui/icons-material/Category";
import PersonIcon from "@mui/icons-material/Person";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

const OrderFilter = ({
  search,
  handleSearchChange,
  productTypeFilter,
  setProductTypeFilter,
  dealerFilter,
  setDealerFilter,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  clearAllFilters,
  hasActiveFilters,
  productTypes,
  dealer,
  totalOrders,
  setPage,
  setRefresh,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(10px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ShoppingCartIcon sx={{ color: "white", fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              sx={{
                color: "white",
                fontWeight: 700,
                textShadow: "0 2px 4px rgba(0,0,0,0.5)",
              }}
            >
              Order Management
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: "rgba(255,255,255,0.9)",
                fontWeight: 400,
              }}
            >
              Track and manage all your orders
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={() => setRefresh(true)}
            sx={{
              color: "white",
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(10px)",
              "&:hover": {
                background: "rgba(255,255,255,0.2)",
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
          {hasActiveFilters && (
            <IconButton
              onClick={clearAllFilters}
              sx={{
                color: "white",
                background: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(10px)",
                "&:hover": {
                  background: "rgba(255,255,255,0.2)",
                },
              }}
            >
              <ClearIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <Paper
        sx={{
          p: 3,
          background: "rgba(255,255,255,0.98)",
          backdropFilter: "blur(20px)",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          border: "1px solid rgba(255,255,255,0.3)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          <FilterListIcon sx={{ color: "#1e3c72" }} />
          <Typography
            variant="h6"
            sx={{ color: "#1e3c72", fontWeight: 600 }}
          >
            Search & Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              size="small"
              label="Active Filters"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search orders..."
              value={search}
              onChange={handleSearchChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: "#1e3c72",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1e3c72",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#666" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Product Type</InputLabel>
              <Select
                value={productTypeFilter}
                onChange={(e) => {
                  setProductTypeFilter(e.target.value);
                  setPage(1);
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <CategoryIcon sx={{ color: "#666", mr: 1 }} />
                  </InputAdornment>
                }
                sx={{
                  borderRadius: 2,
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1e3c72",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1e3c72",
                  },
                }}
              >
                <MenuItem value="">All Types</MenuItem>
                {productTypes?.map((pt) => (
                  <MenuItem key={pt._id} value={pt.name}>
                    {pt.name}
                  </MenuItem>
                ))}
                <MenuItem value="Materials">Materials</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Dealer</InputLabel>
              <Select
                value={dealerFilter}
                onChange={(e) => {
                  setDealerFilter(e.target.value);
                  setPage(1);
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: "#666", mr: 1 }} />
                  </InputAdornment>
                }
                sx={{
                  borderRadius: 2,
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1e3c72",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1e3c72",
                  },
                }}
              >
                <MenuItem value="">All Dealers</MenuItem>
                {dealer?.map((d) => (
                  <MenuItem key={d._id} value={d._id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: "#1e3c72",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1e3c72",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon sx={{ color: "#666" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: "#1e3c72",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1e3c72",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon sx={{ color: "#666" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "40px",
                pl: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: "#666" }}>
                Total: <strong>{totalOrders || 0}</strong>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default OrderFilter;